import { useNavigate } from "react-router";
import { useAuthStore } from "@/stores/authStore";
import * as authApi from "@/api/auth";
import { getProfile } from "@/api/profile";
import { toast } from "sonner";

export function useAuth() {
  const navigate = useNavigate();
  const {
    user,
    setAuth,
    setProfileSetupRequired,
    logout: clearAuth,
    refreshToken,
  } = useAuthStore();

  const navigateAfterAuth = async (role: string) => {
    if (role === "builder") {
      try {
        const profile = await getProfile();
        if (!profile.company_name) {
          setProfileSetupRequired(true);
          navigate("/settings");
          return;
        }
      } catch {
        // If profile fetch fails, redirect to settings to be safe
        setProfileSetupRequired(true);
        navigate("/settings");
        return;
      }
    }
    setProfileSetupRequired(false);
    navigate("/home");
  };

  const login = async (email: string, password: string) => {
    const data = await authApi.login(email, password);
    setAuth(data.user, data.access_token, data.refresh_token);
    await navigateAfterAuth(data.user.role);
  };

  const registerUser = async (token: string, password: string) => {
    const data = await authApi.register({ token, password });
    setAuth(data.user, data.access_token, data.refresh_token);
    await navigateAfterAuth(data.user.role);
  };

  const logout = async () => {
    try {
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch {
      // Ignore logout API errors
    }
    clearAuth();
    toast.success("Logged out");
    navigate("/login");
  };

  return { user, login, registerUser, logout, isAuthenticated: !!user };
}
